import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminVerifyForgotOtp, adminResetPassword, adminResendOtp } from "../Apis/AdminApis";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./AdminLogin.css";

const ResetPassword = ({ identifier, onBack, onSuccess, setPopupMessage, setPopupType, setPopupOpen }) => {
  const [step, setStep] = useState("otp");
  const navigate = useNavigate();
  const trimmed = identifier?.trim();
  const isEmail = trimmed?.includes("@");

  const cleanIdentifier = isEmail
    ? trimmed
    : trimmed?.replace(/\D/g, "");
  
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);

  useEffect(() => {
    if (step !== "otp" || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, step]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleResendOtp = async () => {
    if (timeLeft > 0) return;

    setLoading(true);

    const response = await adminResendOtp(
      isEmail
        ? { email: cleanIdentifier, otp_send_type: "email" }
        : { mobile: cleanIdentifier, otp_send_type: "mobile" }
    );

    setLoading(false);

    if (response.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setPopupMessage("OTP resent successfully");
    setPopupType("success");
    setPopupOpen(true);

    setTimeLeft(120);
    setOtp("");

    document.getElementById("forgot-otp-0")?.focus();
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setPopupMessage("Please enter a valid 6-digit OTP");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }
    if (!isEmail && cleanIdentifier.length < 10) {
      setPopupMessage("Invalid mobile number");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setLoading(true);

    const response = await adminVerifyForgotOtp(
      isEmail
        ? { email: cleanIdentifier, otp }
        : { mobile: cleanIdentifier, otp }
    );

    if (response.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
    } else if (response.message) {
      setPopupMessage(response.message);
      setPopupType("success");
      setPopupOpen(true);
      setStep("reset");
    }

    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setPopupMessage("Please enter a new password");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    if (newPassword.length < 6) {
      setPopupMessage("Password must be at least 6 characters long");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPopupMessage("Passwords do not match");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }

    setLoading(true);

 const response = await adminResetPassword(
  isEmail
    ? {
        email: cleanIdentifier,
        new_password: newPassword,
        confirm_password: confirmPassword
      }
    : {
        mobile: `+91${cleanIdentifier}`,
        new_password: newPassword,
        confirm_password: confirmPassword
      }
);

    if (response.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
    } else if (response.message) {
      setPopupMessage(response.message);
      setPopupType("success");
      setPopupOpen(true);
      onSuccess();
    }

    setLoading(false);
  };

  return (
    <div className="otp-overlay">
      <div className="otp-card">
        <h2 className="otp-title">
          {step === "otp" ? "Verify OTP" : "Reset Password"}
        </h2>

        {step === "otp" ? (
          <>
            <p className="otp-subtitle">
              We've sent a 6-digit OTP to{" "}
              <strong>
                {isEmail
                  ? cleanIdentifier
                  : `+91 ${cleanIdentifier}`}
              </strong>. Please enter it below.
            </p>

            <div className="otp-inputs">
              {otp.split("").concat(Array(6 - otp.length).fill("")).map((digit, index) => (
                <input
                  key={index}
                  id={`forgot-otp-${index}`}
                  maxLength="1"
                  value={digit}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    if (!value) {
                      const updatedOtp = otp.split("");
                      updatedOtp[index] = "";
                      setOtp(updatedOtp.join(""));
                      return;
                    }

                    const updatedOtp = otp.split("").concat(Array(6 - otp.length).fill(""));
                    updatedOtp[index] = value;
                    const finalOtp = updatedOtp.join("").slice(0, 6);
                    setOtp(finalOtp);

                    if (index < 5) {
                      document.getElementById(`forgot-otp-${index + 1}`)?.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace") {
                      const updatedOtp = otp.split("").concat(Array(6 - otp.length).fill(""));

                      if (updatedOtp[index]) {
                        updatedOtp[index] = "";
                        setOtp(updatedOtp.join(""));
                      } else if (index > 0) {
                        document.getElementById(`forgot-otp-${index - 1}`)?.focus();
                      }
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData
                      .getData("text")
                      .replace(/\D/g, "")
                      .slice(0, 6);

                    setOtp(pasted);

                    const focusIndex = Math.min(pasted.length, 5);
                    document.getElementById(`forgot-otp-${focusIndex}`)?.focus();
                  }}
                />
              ))}
            </div>

            <div className="otp-info">
              <span className="otp-timer">⏱ {formatTime(timeLeft)}</span>
              <span
                className="otp-resend"
                onClick={handleResendOtp}
                style={{
                  cursor: timeLeft <= 0 ? "pointer" : "not-allowed",
                  opacity: timeLeft <= 0 ? 1 : 0.5,
                }}
              >
                Resend OTP
              </span>
            </div>

            <div className="otp-actions">
              <button className="btn-backs" onClick={onBack}>
                Back
              </button>
              <button
                className="btn-verify"
                onClick={handleVerifyOtp}
                disabled={loading || otp.length !== 6}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="otp-subtitle">
              Create a new password for your account.
            </p>
            <div className="reset-password-form">
              <div className="input-group">
                <label className="input-label">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="password-input"
                    placeholder="Enter new password"
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowPassword(!showPassword)}
                    role="button"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                 <div className="admin-registration-hint">
                  Password must be at least 6 characters with mix of letters, numbers, and symbols
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="password-input"
                    placeholder="Confirm new password"
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    role="button"
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </div>

              <div className="otp-actions">
                <button className="btn-backs" onClick={() => setStep("otp")}>
                  Back
                </button>
                <button
                  className="btn-verify"
                  onClick={handleResetPassword}
                  disabled={loading}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;